import { useContext, useEffect, useState } from 'react';
import clsx from 'clsx';
import * as R from 'ramda';

import { ITEM_CODE_ATTRIBUTE_NAME } from '@atom/components/common/requests/assetRequestConstants';
import { getAttributeFilterByName } from '@atom/components/common/requests/customTenantUtilities';
import UsersFilter from '@atom/components/common/usersFilter/UsersFilter';
import { GET_ASSETS } from '@atom/graph/asset';
import client from '@atom/graph/client';
import { Progress, SelectAutocomplete, TextField } from '@atom/mui';
import colors from '@atom/styles/colors';
import { AssetRequestCreateInput } from '@atom/types/assetRequest';
import {
  AssetBudget,
  AssetConnectionItem,
  AssetsConnection,
  AssetsConnectionInput,
} from '@atom/types/inventory';

import RequestsContext from '../../RequestsContext';

import classes from './sddotCreate.module.css';

const ASSET_LIMIT = 250;

const styles = {
  label: {
    color: colors.neutral.dim,
  },
};

interface Props {
  budget: AssetBudget;
  createInputs: AssetRequestCreateInput;
  updateCreateInput: (
    value: any,
    property: keyof AssetRequestCreateInput,
  ) => void;
  selectedFromAsset: AssetConnectionItem;
  setSelectedFromAsset: (asset: AssetConnectionItem) => void;
  // TODO: [AM-16707]: Temp code until BE solution is built
  totalRemaining: number;
  fromTotalRemaining: number;
  totalRemainingLoading: boolean;
  fromTotalRemainingLoading: boolean;
}

const SDDOTInventoryCreate = ({
  budget,
  createInputs,
  updateCreateInput,
  selectedFromAsset,
  setSelectedFromAsset,
  // TODO: [AM-16707]: Temp code until BE solution is built
  totalRemaining,
  fromTotalRemaining,
  totalRemainingLoading,
  fromTotalRemainingLoading,
}: Props) => {
  const { asset } = useContext(RequestsContext);

  const [assets, setAssets] = useState<AssetConnectionItem[]>([]);
  const [loadingAssets, setLoadingAssets] = useState<boolean>(false);

  const getAssetPage = (page: number): Promise<AssetConnectionItem[]> => {
    const itemCodeAttributeFilter = getAttributeFilterByName(
      asset,
      ITEM_CODE_ATTRIBUTE_NAME,
    );

    return client
      .query<{ assets: AssetsConnection }, { input: AssetsConnectionInput }>({
        query: GET_ASSETS,
        variables: {
          input: {
            page,
            limit: ASSET_LIMIT,
            attributes: itemCodeAttributeFilter,
            omitIds: [asset.id],
          },
        },
        fetchPolicy: 'network-only',
      })
      .then(({ data }) => data.assets.assets);
  };

  useEffect(() => {
    // Batch load all assets upfront
    const retrieveAssets = async () => {
      setLoadingAssets(true);

      const itemCodeAttributeFilter = getAttributeFilterByName(
        asset,
        ITEM_CODE_ATTRIBUTE_NAME,
      );

      const { data } = await client.query<
        { assets: AssetsConnection },
        { input: AssetsConnectionInput }
      >({
        query: GET_ASSETS,
        variables: {
          input: {
            page: 1,
            limit: ASSET_LIMIT,
            attributes: itemCodeAttributeFilter,
            omitIds: [asset.id],
          },
        },
        fetchPolicy: 'network-only',
      });

      let totalAssets = data.assets.assets;
      const total = Number(data.assets.totalCount);

      if (total > ASSET_LIMIT) {
        const pages = Math.ceil(total / ASSET_LIMIT);
        const promises: Array<Promise<AssetConnectionItem[]>> = [];

        for (let index = 2; index <= pages; index++) {
          promises.push(getAssetPage(index));
        }

        const allRemainingAssets = await Promise.all(promises);
        totalAssets = [...totalAssets, ...R.flatten(allRemainingAssets)];
      }

      setAssets(totalAssets);
      setLoadingAssets(false);
    };

    retrieveAssets();
  }, []);

  const handleAssetSelect = (newAsset: AssetConnectionItem) => {
    setSelectedFromAsset(newAsset);
    updateCreateInput(newAsset?.id, 'fromAssetId');
  };

  const numberUpdate = (
    value: any,
    property: keyof AssetRequestCreateInput,
  ) => {
    const updatedValue =
      Math.abs(Number(value)) > 0 ? Math.abs(Number(value)) : null;
    updateCreateInput(updatedValue, property);
  };

  const renderOption = (option: AssetConnectionItem) => {
    return (
      <div>
        <div>{option.name}</div>
        <div
          className={classes.assetOptionSubtext}
        >{`${option.budget.quantityOnHand} ${option.budget.unit} in stock`}</div>
      </div>
    );
  };

  // TODO: [AM-16707]: Temp code until BE solution is built
  const requestedQuantityError =
    Number(fromTotalRemaining) - Number(createInputs?.quantityOrdered) < 0;

  // const requestedQuantityError =
  //   Number(selectedFromAsset?.budget?.quantityOnHand) -
  //     Number(createInputs?.quantityOrdered) <
  //   0;

  return (
    <>
      <div className={classes.inputContainer}>
        <SelectAutocomplete
          id="fromAssetId"
          label="Transfer From"
          options={assets}
          value={selectedFromAsset}
          onChange={(_, newValue) => handleAssetSelect(newValue)}
          renderOption={renderOption}
          getOptionLabel={option => option.name}
          disabled={loadingAssets}
          loading={loadingAssets}
          InputLabelProps={{ style: styles.label }}
          blurOnSelect
          data-cy="assetRequestInventoryTransferFromAsset"
        />
        {selectedFromAsset && (
          // <div className={clsx(classes.subtext, classes.selectedAsset)}>{`${selectedFromAsset?.budget?.quantityOnHand} ${selectedFromAsset?.budget?.unit} in stock`}</div>
          <div className={clsx(classes.subtext, classes.selectedAsset)}>
            {fromTotalRemainingLoading ? (
              <Progress size={10} style={{ justifyContent: 'flex-start' }} />
            ) : (
              `${fromTotalRemaining} ${selectedFromAsset?.budget?.unit} in stock`
            )}
          </div>
        )}
      </div>
      <div className={classes.inputContainer}>
        <div className={clsx(classes.subtext, classes.title)}>Transfer To</div>
        <div>{asset?.name}</div>
        {/* <div className={classes.subtext}>{`${budget?.quantityOnHand} ${budget?.unit} in stock`}</div> */}
        {totalRemainingLoading ? (
          <Progress size={10} style={{ justifyContent: 'flex-start' }} />
        ) : (
          <div
            className={classes.subtext}
          >{`${totalRemaining} ${budget?.unit} in stock`}</div>
        )}
      </div>
      <div className={classes.inputContainer}>
        <TextField
          slotProps={{ inputLabel: { style: styles.label } }}
          type="number"
          value={createInputs?.quantityOrdered || ''}
          label={`Requested Quantity (${budget?.unit})`}
          onChange={event =>
            numberUpdate(event.target.value, 'quantityOrdered')
          }
          helperText={
            requestedQuantityError && 'Exceeds available stock levels'
          }
          error={requestedQuantityError}
          data-cy="assetRequestInventoryTransferQuantity"
        />
      </div>
      {createInputs?.quantityOrdered && (
        <div className={classes.quantityTransferSubtext}>
          The unit cost of this inventory item will be re-calculated by taking
          into account the approved quantity and the unit cost of the inventory
          it was transferred from.
        </div>
      )}
      <div className={classes.inputContainer}>
        <UsersFilter
          value={createInputs?.assignedTo}
          updateValue={newValue => updateCreateInput(newValue, 'assignedTo')}
          placeholder="Search users"
          label="Assigned To"
          showTitle
          data-cy="assetRequestInventoryTransferAssignedTo"
        />
      </div>
    </>
  );
};

export default SDDOTInventoryCreate;
